import React, { useState } from 'react'
import { Clock, Activity, CheckCircle } from 'lucide-react'
import { AttendanceFilters } from '../components/attendance/AttendanceFilters'
import { AttendanceTable } from '../components/attendance/AttendanceTable'
import { AttendanceEventDetails } from '../components/attendance/AttendanceEventDetails'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Pagination,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useAttendanceEvents } from '../hooks/useAttendance'
import { useEmployees } from '../hooks/useEmployees'
import { useLocations } from '../hooks/locations/useLocations'
import type { AttendanceEvent, AttendanceEventFilters } from '../types/attendance.types'

export function Attendance() {
  const [filters, setFilters] = useState<AttendanceEventFilters>({
    page: 1,
    limit: 20,
    sort: 'DESC',
  })

  const [selectedEvent, setSelectedEvent] = useState<AttendanceEvent | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)

  const { data, isLoading, error, refetch } = useAttendanceEvents(filters)
  const { data: employees } = useEmployees({ limit: 10000 })
  const { data: locations } = useLocations({ limit: 1000 })

  const handleFiltersChange = (newFilters: Partial<AttendanceEventFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleViewDetails = (event: AttendanceEvent) => {
    setSelectedEvent(event)
    setShowDetailsModal(true)
  }

  const handleCloseDetails = () => {
    setShowDetailsModal(false)
    setSelectedEvent(null)
  }

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!data?.data) return null

    const events = data.data
    const checkIns = events.filter((e) => e.event_type === 'check_in').length
    const checkOuts = events.filter((e) => e.event_type === 'check_out').length
    const verified = events.filter((e) => e.verification_status === 'verified').length

    return {
      total: data.total,
      checkIns,
      checkOuts,
      verified,
    }
  }, [data])

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">События посещаемости</h1>
          <p className="text-muted-foreground">
            Мониторинг и управление событиями прихода/ухода сотрудников
          </p>
        </div>
        <ErrorState title="Ошибка загрузки событий посещаемости" onAction={refetch} />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">События посещаемости</h1>
        <p className="text-muted-foreground">
          Мониторинг и управление событиями прихода/ухода сотрудников
        </p>
      </div>

      {/* Filters */}
      <AttendanceFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        employees={employees}
        locations={locations}
      />

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Всего событий</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.checkIns}</p>
                  <p className="text-sm text-muted-foreground">Приходов</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-red-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.checkOuts}</p>
                  <p className="text-sm text-muted-foreground">Уходов</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.verified}</p>
                  <p className="text-sm text-muted-foreground">Верифицировано</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список событий</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} событий`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <AttendanceTable events={data.data} onViewDetails={handleViewDetails} />

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                События не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или выберите другой период.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Details Modal */}
      <AttendanceEventDetails
        event={selectedEvent}
        open={showDetailsModal}
        onClose={handleCloseDetails}
      />
    </div>
  )
}